import React, { useState } from 'react';
import './AuthPage.css';
import { useUser } from './UserContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../common/Icon/Icon';

const passwordRequirements = [
    {
        label: 'At least 8 characters',
        test: (pw) => pw.length >= 8,
    },
    {
        label: 'At least one symbol (!@#$%^&*.)',
        test: (pw) => /[!@#$%^&*.]/.test(pw),
    },
    {
        label: 'At least one number (0-9)',
        test: (pw) => /[0-9]/.test(pw),
    },
];

function AuthPage() {
    const [tab, setTab] = useState('login');
    const [loginData, setLoginData] = useState({ emailOrUsername: '', password: '' });
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [signupData, setSignupData] = useState({ email: '', username: '', password: '', confirmPassword: '' });
    const [showSignupPassword, setShowSignupPassword] = useState(false);
    const [showSignupConfirm, setShowSignupConfirm] = useState(false);
    const [showPasswordReqs, setShowPasswordReqs] = useState(false);
    const [error, setError] = useState('');
    const [signupSuccess, setSignupSuccess] = useState(false);
    // Forgot-password mini flow
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotMsg, setForgotMsg] = useState('');
    const { setUser } = useUser();
    const navigate = useNavigate();

    const sendReset = async () => {
        if (!forgotEmail.trim()) return;
        try {
            const res = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail.trim() }),
            });
            const d = await res.json();
            setForgotMsg(d.message || 'If that email has an account, a reset link is on its way.');
        } catch {
            setForgotMsg('Could not send the reset email. Try again in a minute.');
        }
    };

    const handleTabChange = (newTab) => {
        setTab(newTab);
        setError('');
    };

    const handleLoginChange = (e) => {
        setLoginData({ ...loginData, [e.target.name]: e.target.value });
    };

    const handleRememberMeChange = (e) => {
        setRememberMe(e.target.checked);
    };

    const handleSignupChange = (e) => {
        setSignupData({ ...signupData, [e.target.name]: e.target.value });
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginData.emailOrUsername || !loginData.password) {
            setError('Please enter your email/username and password.');
            return;
        }
        setError('');
        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ ...loginData, rememberMe })
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Failed to log in.');
                return;
            }
            setUser(data.user);
            navigate('/');
        } catch {
            setError('Failed to log in.');
        }
    };

    // Password requirements check for sign up
    const passwordChecks = passwordRequirements.map(req => req.test(signupData.password));

    const handleSignup = async (e) => {
        e.preventDefault();
        if (!signupData.email || !signupData.username || !signupData.password || !signupData.confirmPassword) {
            setError('Please fill in all fields.');
            return;
        }
        if (signupData.password !== signupData.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (signupData.username.length > 25) {
            setError('Username must be 25 characters or fewer.');
            return;
        }
        if (!passwordChecks.every(Boolean)) {
            setError('Password does not meet all requirements.');
            return;
        }
        setError('');
        try {
            const res = await fetch('/api/auth/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    email: signupData.email,
                    username: signupData.username,
                    password: signupData.password
                })
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Failed to sign up.');
                return;
            }
            setSignupData({ email: '', username: '', password: '', confirmPassword: '' });
            setSignupSuccess(true);
        } catch {
            setError('Failed to sign up.');
        }
    };

    // Eye icon event handlers
    const handleEyeDown = (setter) => () => setter(true);
    const handleEyeUp = (setter) => () => setter(false);
    const handleEyeLeave = (setter) => () => setter(false);

    // Password requirements show/hide logic
    const handlePasswordFocus = () => setShowPasswordReqs(true);
    const handlePasswordBlur = () => setShowPasswordReqs(false);

    return (
        <div className="authpage-container">
            <div className="authpage-box">
                {signupSuccess ? (
                    <div className="authpage-success-box">
                        <div className="authpage-success-message">Signed up successfully!</div>
                        <button className="authpage-submit" onClick={() => { setSignupSuccess(false); handleTabChange('login'); }}>Log In</button>
                    </div>
                ) : (
                    <>
                        <div className="authpage-tabs">
                            <button
                                className={tab === 'login' ? 'active' : ''}
                                onClick={() => handleTabChange('login')}
                            >
                                Log In
                            </button>
                            <button
                                className={tab === 'signup' ? 'active' : ''}
                                onClick={() => handleTabChange('signup')}
                            >
                                Sign Up
                            </button>
                        </div>
                        <div className="authpage-content">
                            {tab === 'login' ? (
                                <form onSubmit={handleLogin} className="authpage-form">
                                    <label>Email or Username</label>
                                    <input
                                        type="text"
                                        name="emailOrUsername"
                                        value={loginData.emailOrUsername}
                                        onChange={handleLoginChange}
                                        autoFocus
                                        autoComplete="username"
                                    />
                                    <label>Password</label>
                                    <div className="authpage-password-row">
                                        <input
                                            type={showLoginPassword ? 'text' : 'password'}
                                            name="password"
                                            value={loginData.password}
                                            onChange={handleLoginChange}
                                            autoComplete="current-password"
                                            className="authpage-password-input"
                                        />
                                        <button
                                            type="button"
                                            className="authpage-eye-btn"
                                            tabIndex={-1}
                                            aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                                            onMouseDown={handleEyeDown(setShowLoginPassword)}
                                            onMouseUp={handleEyeUp(setShowLoginPassword)}
                                            onMouseLeave={handleEyeLeave(setShowLoginPassword)}
                                        >
                                            <Icon name="eye" size={18} />
                                        </button>
                                    </div>
                                    <div className="authpage-remember-row">
                                        <input
                                            type="checkbox"
                                            id="rememberMe"
                                            checked={rememberMe}
                                            onChange={handleRememberMeChange}
                                        />
                                        <label htmlFor="rememberMe" className="authpage-remember-label">Remember me</label>
                                    </div>
                                    {error && <div className="authpage-error">{error}</div>}
                                    <button type="submit" className="authpage-submit">Log In</button>
                                    <button
                                        type="button"
                                        className="authpage-forgot"
                                        onClick={() => { setForgotOpen(o => !o); setForgotMsg(''); }}
                                    >
                                        Forgot your password?
                                    </button>
                                    {forgotOpen && (
                                        <div className="authpage-forgot-box">
                                            {forgotMsg ? (
                                                <p className="authpage-forgot-msg">{forgotMsg}</p>
                                            ) : (
                                                <>
                                                    <input
                                                        type="email"
                                                        placeholder="Your account email"
                                                        value={forgotEmail}
                                                        onChange={e => setForgotEmail(e.target.value)}
                                                    />
                                                    <button type="button" className="authpage-submit" onClick={sendReset}>
                                                        Send reset link
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </form>
                            ) : (
                                <form onSubmit={handleSignup} className="authpage-form">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={signupData.email}
                                        onChange={handleSignupChange}
                                        autoComplete="email"
                                    />
                                    <label>Username</label>
                                    <input
                                        type="text"
                                        name="username"
                                        value={signupData.username}
                                        onChange={handleSignupChange}
                                        autoComplete="username"
                                        maxLength={25}
                                        required
                                    />
                                    <label>Password</label>
                                    <div className="authpage-password-row">
                                        <input
                                            type={showSignupPassword ? 'text' : 'password'}
                                            name="password"
                                            value={signupData.password}
                                            onChange={handleSignupChange}
                                            autoComplete="new-password"
                                            className="authpage-password-input"
                                            onFocus={handlePasswordFocus}
                                            onBlur={handlePasswordBlur}
                                        />
                                        <button
                                            type="button"
                                            className="authpage-eye-btn"
                                            tabIndex={-1}
                                            aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
                                            onMouseDown={handleEyeDown(setShowSignupPassword)}
                                            onMouseUp={handleEyeUp(setShowSignupPassword)}
                                            onMouseLeave={handleEyeLeave(setShowSignupPassword)}
                                        >
                                            <Icon name="eye" size={18} />
                                        </button>
                                    </div>
                                    {showPasswordReqs && (
                                        <div className="authpage-password-reqs">
                                            {passwordRequirements.map((req, i) => (
                                                <div key={req.label} className={passwordChecks[i] ? 'met' : 'unmet'}>
                                                    <Icon name={passwordChecks[i] ? 'check' : 'x'} /> {req.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <label>Confirm Password</label>
                                    <div className="authpage-password-row">
                                        <input
                                            type={showSignupConfirm ? 'text' : 'password'}
                                            name="confirmPassword"
                                            value={signupData.confirmPassword}
                                            onChange={handleSignupChange}
                                            autoComplete="new-password"
                                            className="authpage-password-input"
                                        />
                                        <button
                                            type="button"
                                            className="authpage-eye-btn"
                                            tabIndex={-1}
                                            aria-label={showSignupConfirm ? 'Hide password' : 'Show password'}
                                            onMouseDown={handleEyeDown(setShowSignupConfirm)}
                                            onMouseUp={handleEyeUp(setShowSignupConfirm)}
                                            onMouseLeave={handleEyeLeave(setShowSignupConfirm)}
                                        >
                                            <Icon name="eye" size={18} />
                                        </button>
                                    </div>
                                    {error && <div className="authpage-error">{error}</div>}
                                    <button type="submit" className="authpage-submit">Sign Up</button>
                                </form>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AuthPage; 